import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { LLC, isLLCFormed } from "../legal";

const TIER_PRICES = { free: "0", pro: "199", premium: "599" };

export default function SubscriptionSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const { user, refreshUser } = useAuth();
  const { lang } = useI18n();
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/subscriptions/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= maxAttempts) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, refreshUser]);

  const tier = details?.tier || "pro";
  const price = TIER_PRICES[tier] || "—";
  const today = new Date().toISOString().slice(0, 10);
  const receiptId = sessionId ? `CD-RCT-${sessionId.slice(-10).toUpperCase()}` : "—";

  return (
    <div className="container-cd py-16" data-testid="subscription-success-page">
      <div className="max-w-3xl">
        <div className="overline">PAYMENT</div>
        {status === "checking" && (
          <>
            <h1 className="font-display text-5xl mt-6">
              {lang === "es" ? "Confirmando tu suscripción…" : "Confirming your subscription…"}
            </h1>
            <p className="mt-4 font-mono text-sm" style={{ color: "var(--ink-2)" }}>
              {lang === "es"
                ? "Consultando a Stripe. Toma unos segundos."
                : "Polling Stripe. This usually takes a few seconds."}
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <h1 className="font-display text-5xl mt-6" style={{ color: "var(--orange)" }}>
              {lang === "es" ? "Estás dentro." : "You're in."}
            </h1>
            <p className="mt-4 text-lg" style={{ color: "var(--ink-2)" }}>
              {lang === "es" ? "Suscripción activa: " : "Subscription active: "}
              <strong>{tier.toUpperCase()}</strong>.{" "}
              {lang === "es"
                ? "Tu perfil ahora es elegible para el corredor."
                : "Your profile is now corridor-eligible."}
            </p>

            <div className="mt-6 flex flex-wrap gap-3 no-print">
              <button
                onClick={() => nav("/dashboard")}
                className="btn btn-primary btn-lg"
                data-testid="success-to-dashboard"
              >
                {lang === "es" ? "Ir al panel" : "Go to dashboard"}
              </button>
              <button
                onClick={() => window.print()}
                className="btn btn-dark btn-lg"
                data-testid="success-print-receipt"
              >
                <Printer size={14} />{" "}
                {lang === "es" ? "Imprimir recibo" : "Print receipt"}
              </button>
            </div>

            {/* Printable Receipt */}
            <article className="receipt mt-12 cd-card p-10" data-testid="subscription-receipt">
              <div className="flex items-center justify-between pb-4" style={{ borderBottom: "2px solid var(--ink)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center font-display text-white" style={{ background: "var(--ink)" }}>CD</div>
                  <div className="leading-tight">
                    <div className="font-display text-base">CORRIDOR DIGITAL</div>
                    <div className="overline" style={{ fontSize: 9 }}>
                      {lang === "es" ? "RECIBO DE SUSCRIPCIÓN" : "SUBSCRIPTION RECEIPT"}
                    </div>
                  </div>
                </div>
                <div className="text-right font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  {receiptId}
                  <br />
                  {today}
                </div>
              </div>

              <h2 className="font-display text-3xl mt-8">
                {lang === "es" ? "Recibo · Suscripción" : "Receipt · Subscription"}
              </h2>

              <div className="mt-8 grid grid-cols-2 gap-6">
                <div>
                  <div className="overline">{lang === "es" ? "FACTURADO A" : "BILLED TO"}</div>
                  <div className="mt-3 font-mono text-xs space-y-1">
                    <div><strong>{user?.name || "—"}</strong></div>
                    <div>{user?.email || "—"}</div>
                  </div>
                </div>
                <div>
                  <div className="overline">{lang === "es" ? "EMITIDO POR" : "ISSUED BY"}</div>
                  <div className="mt-3 font-mono text-xs space-y-1">
                    <div><strong>{isLLCFormed ? LLC.name : LLC.pending_label}</strong></div>
                    <div>{LLC.registered_address_line_1}</div>
                    <div>{LLC.registered_address_line_2}</div>
                    <div style={{ color: "var(--ink-2)" }}>{LLC.support_email}</div>
                  </div>
                </div>
              </div>

              <div className="mt-10" style={{ border: "1px solid var(--line)" }}>
                <table className="w-full font-mono text-xs">
                  <thead>
                    <tr style={{ background: "var(--surface)" }}>
                      <th className="text-left p-3 overline">
                        {lang === "es" ? "DESCRIPCIÓN" : "DESCRIPTION"}
                      </th>
                      <th className="text-right p-3 overline">
                        {lang === "es" ? "MONTO" : "AMOUNT"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3" style={{ borderTop: "1px solid var(--line)" }}>
                        Corridor Digital — {tier.toUpperCase()}{" "}
                        {lang === "es" ? "mensual" : "monthly"}
                      </td>
                      <td className="p-3 text-right font-heading" style={{ borderTop: "1px solid var(--line)" }}>
                        ${price} USD
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 overline" style={{ borderTop: "1px solid var(--line)", background: "var(--surface)" }}>
                        {lang === "es" ? "TOTAL CARGADO" : "TOTAL CHARGED"}
                      </td>
                      <td className="p-3 text-right font-display text-xl" style={{ borderTop: "1px solid var(--line)", background: "var(--surface)" }}>
                        ${price} USD
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                <div>
                  <div className="overline">{lang === "es" ? "MÉTODO DE PAGO" : "PAYMENT METHOD"}</div>
                  <div className="mt-2">Stripe · {lang === "es" ? "Pagado" : "Paid"} · {today}</div>
                </div>
                <div>
                  <div className="overline">{lang === "es" ? "ID DE TRANSACCIÓN" : "TRANSACTION ID"}</div>
                  <div className="mt-2 break-all">{sessionId || "—"}</div>
                </div>
              </div>

              <p className="mt-10 text-xs" style={{ color: "var(--ink-2)" }}>
                {lang === "es" ? (
                  <>
                    Gracias por suscribirte. Para consultas de facturación contacta a{" "}
                    <a className="underline" href={`mailto:${LLC.support_email}`}>{LLC.support_email}</a>.
                    Reembolsos sujetos a la Política de Reembolsos publicada en corridordigital.net/legal/refund.
                  </>
                ) : (
                  <>
                    Thank you for subscribing. For billing inquiries contact{" "}
                    <a className="underline" href={`mailto:${LLC.support_email}`}>{LLC.support_email}</a>.
                    Refunds subject to the Refund Policy published at corridordigital.net/legal/refund.
                  </>
                )}
              </p>
            </article>

            <style>{`
              @media print {
                .no-print { display: none !important; }
                header, footer, [data-testid="site-footer"], [data-testid="main-nav"], [data-testid="cookie-banner"] { display: none !important; }
                .receipt { border: none !important; padding: 0 !important; }
                body, .container-cd { background: white !important; }
                @page { size: A4; margin: 14mm; }
              }
            `}</style>
          </>
        )}

        {status === "pending" && (
          <>
            <h1 className="font-display text-5xl mt-6">
              {lang === "es" ? "Aún procesando…" : "Still processing…"}
            </h1>
            <p className="mt-4" style={{ color: "var(--ink-2)" }}>
              {lang === "es"
                ? "Vuelve en un momento. Tu plan se actualizará automáticamente."
                : "Check back in a minute. Your tier will update automatically."}
            </p>
          </>
        )}

        {(status === "expired" || status === "error") && (
          <>
            <h1 className="font-display text-5xl mt-6" style={{ color: "var(--red)" }}>
              {lang === "es" ? "Algo salió mal." : "Something went sideways."}
            </h1>
            <p className="mt-4" style={{ color: "var(--ink-2)" }}>
              {lang === "es"
                ? "El pago no se completó. Intenta de nuevo desde precios."
                : "Your payment didn't complete. Try again from pricing."}
            </p>
            <button onClick={() => nav("/pricing")} className="btn btn-primary btn-lg mt-8">
              {lang === "es" ? "Volver a precios" : "Back to pricing"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
